import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useConst } from '@execonline-inc/react-hooks.private';
import { findLink } from '../../../../../LinkyLinky';
import { T } from '../../../../../Translations';
import LearningCollectionEditStore from '../../Edit/Store';
import { ReadyDeepLinks } from '../../Edit/Store/Types';
import Nav from '../../Nav';
import Banner from './Banner';
import Breadcrumbs from './Breadcrumbs';
import CreateShareUrlStore from './CreateShareUrlStore';
import CreateShareUrlReactions from './CreateShareUrlStore/Reactions';
import InviteParticipantModal from './InviteParticipantModal';
import InviteParticipantReactions from './InviteParticipantModal/Reactions';
import InviteParticipantStore from './InviteParticipantModal/Store';
import LinksTable from './LinksTable';
import PublishingReactions from './Publish/Reactions';
import PublishingStore from './Publish/Store';
import ClickableTooltip from '../../../../ClickableTooltip';
import TooltipIcon from '../../../../Cutesies/TooltipIcon';
import { emptyFragment } from '../../../../../EmptyFragment';
import { Link } from '../../../../../Resource/Types';
import DeepLinksOptionsStore from './DeepLinksOptionsStore';
import DeepLinksOptionsReactions from './DeepLinksOptionsStore/Reactions';
import Filters from './Filters';
import DisplayedCount from './Filters/DisplayedCount';

interface Props {
  learningCollectionStore: LearningCollectionEditStore;
  state: ReadyDeepLinks;
}

function DeepLinksBody({ learningCollectionStore, state }: Props) {
  const { learningCollectionResource } = state;
  const { payload } = learningCollectionResource;

  const store = useConst(() => new CreateShareUrlStore(learningCollectionResource));
  const publishingStore = useConst(
    () =>
      new PublishingStore(
        findLink('publish', learningCollectionResource.links),
        learningCollectionResource,
      ),
  );
  const inviteParticipantStore = useConst(() => new InviteParticipantStore());
  const handleDownload = (
    link: Link,
    totalDeepLinkCount: number,
    filteredDeepLinkCount: number,
  ) => {
    inviteParticipantStore.downloadProgram(link, totalDeepLinkCount, filteredDeepLinkCount);
    inviteParticipantStore.modalStore.open();
  };
  const deepLinksOptionsStore = useConst(
    () => new DeepLinksOptionsStore(state.learningCollectionResource.links),
  );

  useEffect(() => {
    deepLinksOptionsStore.loading();
  }, [deepLinksOptionsStore]);

  return (
    <>
      <div className="bg-white mx-auto max-w-clientPortalMaxWidth px-2.5 pt-2.5 lg:px-12 lg:pt-4 border-y-1">
        <Breadcrumbs name={payload.name} />
        <div className="flex flex-col">
          <h2 className="text-xl font-semibold text-gray-900">
            <T kind="Manage Participant Access" />
          </h2>
          <Nav id={payload.id} />
        </div>
      </div>
      <Banner learningCollection={payload} store={store} publishing={publishingStore.publishing} />
      <div className="max-w-clientPortalMaxWidth px-2.5 lg:px-12 lg:my-4 pb-4 pt-8 flex justify-between">
        <div className="flex items-center">
          <h3 className="text-xl font-semibold text-gray-900 mr-2">
            <T kind="Program Access" />
          </h3>
          <ClickableTooltip
            tooltipId={'Program Access'}
            tooltipIcon={<TooltipIcon />}
            className="items-center flex"
          >
            <h3>
              <T kind="Use Program Deep Links to promote specific programs in this collection..." />
            </h3>
          </ClickableTooltip>
        </div>
        {findLink('program-deep-link-import', learningCollectionResource.links)
          .map((link) => (
            <button
              onClick={() =>
                handleDownload(
                  link,
                  learningCollectionResource.payload.totalShareUrlCount,
                  learningCollectionResource.payload.filteredShareUrlCount,
                )
              }
              className="rounded-md bg-teal-600 px-3 py-2 text-white"
            >
              <T kind="Download Program Deep Links in CSV" />
            </button>
          ))
          .getOrElse(emptyFragment)}
      </div>
      <Filters
        duplicateProgramDeepLinkCount={
          state.learningCollectionResource.payload.duplicateProgramDeepLinkCount
        }
        optionsStore={deepLinksOptionsStore}
        searchParameters={state.deepLinksSearchParams}
        updatingSearchParameters={learningCollectionStore.updatingDeepLinksSearchParams}
      />
      <DisplayedCount
        totalShareUrlCount={learningCollectionResource.payload.totalShareUrlCount}
        filteredShareUrlCount={learningCollectionResource.payload.filteredShareUrlCount}
      />
      <LinksTable
        programFamilyShareUrls={payload.programFamilyShareUrls}
        store={store}
        inviteParticipantStore={inviteParticipantStore}
      />
      <InviteParticipantModal
        links={learningCollectionResource.links}
        inviteParticipantStore={inviteParticipantStore}
      />
      <CreateShareUrlReactions
        fireImmediately
        store={store}
        learningCollectionStore={learningCollectionStore}
        learningCollectionResource={learningCollectionResource}
      />
      <PublishingReactions
        store={publishingStore}
        learningCollectionResource={learningCollectionResource}
        learningCollectionStore={learningCollectionStore}
      />
      <InviteParticipantReactions
        store={inviteParticipantStore}
        learningCollectionResource={learningCollectionResource}
        fireImmediately={true}
      />
      <DeepLinksOptionsReactions store={deepLinksOptionsStore} fireImmediately={true} />
    </>
  );
}

export default observer(DeepLinksBody);
